import path from 'path';

import { ReplaceOperation } from 'fast-json-patch';

import { OffsetPagedResults } from '~interfaces';
import { SearchPagedParameters } from '~interfaces/requests';
import { BaseReferenceResponse, SkcOffsetPagedResponse } from '~interfaces/responses';
import { TopologyProxiedSkopeiConnectService } from '~services';

import NfcTagTypeEnum from '../enums/nfcTagTypeEnum';
import NfcTag from '../interfaces/nfcTag';

class SkcKeysService extends TopologyProxiedSkopeiConnectService {
	public readonly path = 'keys';

	/**
	 * Get a list of keys (aka nfc tags and users)
	 * @param page The number of the page
	 * @param pageSize The amount of results of the page
	 * @returns
	 */
	async getKeys({
		page = 1,
		pageSize = 10,
		includeUsers,
		...args
	}: { includeUsers?: boolean } & SearchPagedParameters): Promise<OffsetPagedResults<NfcTag>> {
		const { data } = await this._client.get<SkcOffsetPagedResponse<KeyResponse>>(this.path, {
			params: {
				'page-number': page,
				'page-size': pageSize,
				'organisation-id': args.organisationId,
				'virtual-key-enabled': includeUsers,
				'search-query': args.searchQuery || undefined,
			},
		});

		return this.mapOffsetPagedResponse(data, SkcKeysService.fromResponse);
	}

	async getKeyId(id: string): Promise<NfcTag> {
		const { data } = await this._client.get<{ data: KeyResponse }>(path.join(this.path, id));

		return SkcKeysService.fromResponse(data.data);
	}

	async patchKey(nfcTagId: string, data: NfcTag): Promise<null> {
		const content = SkcKeysService.toRequest(data);
		const patches: ReplaceOperation<RfidCardRequest[keyof RfidCardRequest]>[] = Object.keys(
			content
		).map((el) => ({
			op: 'replace',
			path: `/${el}`,
			value: content[el as keyof RfidCardRequest],
		}));
		await this._client.patch(path.join(this.path, nfcTagId), patches);

		return null;
	}

	async putKey(nfcTagId: string, data: NfcTag): Promise<null> {
		const content = SkcKeysService.toRequest(data);
		await this._client.put(path.join(this.path, nfcTagId), content);

		return null;
	}

	static fromResponse(data: KeyResponse): NfcTag {
		const { id, skcId, name, serialNumber, encryptionEnabled, idToken, organisation, ...rest } =
			data;

		return {
			...rest,
			...SkcKeysService.fromBaseReferenceResponse({
				id: id ?? skcId,
				name: name,
			}),
			skcId: skcId?.toString(),
			tagNumber: serialNumber,
			tagType: encryptionEnabled ? NfcTagTypeEnum.Skopei : NfcTagTypeEnum.External,
			rfidUid: idToken,
			organisation: SkcKeysService.fromBaseReferenceResponse(organisation),
		};
	}

	static toRequest(data: NfcTag): RfidCardRequest {
		const { id, organisation, rfidUid, tagType, tagNumber, label, ...rest } = data;

		return {
			...rest,
			encryptionEnabled: tagType === NfcTagTypeEnum.Skopei,
			serialNumber: tagNumber,
			idToken: rfidUid,
			name: data.label,
		};
	}
}

// This is validated for the tripsinsights
// Different for the normal trip?
interface KeyResponse extends BaseReferenceResponse {
	skcId: number;
	serialNumber?: string; // Should only be present when it is Skopei tag
	// keyType: 'skopeiSecureTag' | 'externalTag' | 'virtualKey' | 'externalTag_VirtualKey' | 'skopeiSecureTag_VirtualKey';
	encryptionEnabled: boolean;
	virtualKeyEnabled: boolean;
	idToken: string;
	organisation: BaseReferenceResponse;
}

interface RfidCardRequest {
	name?: string;
	serialNumber: string;
	idToken: string;
	encryptionEnabled: boolean;
	virtualKeyEnabled: boolean;
}

export default SkcKeysService;
